import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

// Medium - for secondary actions
//<Button size="medium" variant="default">Save</Button>

// Large - for primary CTAs (default)
//<Button>Get Started</Button>
//<Button size="large">Submit Form</Button>

// Wider - for full-width style
//<Button size="wider">Continue to Payment</Button>

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-all cursor-pointer disabled:cursor-not-allowed [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-background hover:bg-primary-hover active:bg-primary-active disabled:bg-muted disabled:text-gray-500",
        link: "text-primary font-medium underline-offset-4 hover:text-primary-hover hover:underline active:text-primary-active data-[disabled=true]:text-muted data-[disabled=true]:no-underline data-[disabled=true]:cursor-not-allowed",
        inverted:
          "text-primary bg-primary/10 hover:bg-primary/20 active:bg-primary/25 disabled:bg-gray-100 disabled:text-muted"
      },
      size: {
        small: "py-1.5 px-2 xs:py-1.5 xs:px-3 text-sm sm:text-base leading-4 sm:leading-5 rounded-lg",
        medium: "py-2 px-3 xs:py-2 xs:px-4 text-base sm:text-lg leading-5 sm:leading-6 rounded-xl",
        large: "py-3 px-5 xs:py-2 xs:px-7 text-xl sm:text-2xl leading-6 sm:leading-8 rounded-2xl",
        wider: "py-2 px-10 xs:px-14 text-xl sm:text-2xl leading-6 sm:leading-8 rounded-2xl",
      }
    },
    defaultVariants: {
      size: "large",
      variant: "default"
    }
  }
);

function Button({
  size,
  variant,
  disabled,
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  const patchForLink =
    asChild && disabled
      ? {
        "data-disabled": true,
        "aria-disabled": true,
        tabIndex: -1,
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }
      }
      : {};

  return (
    <Comp
      data-slot="button"
      disabled={asChild ? undefined : disabled}
      className={cn(buttonVariants({ variant, size }), className)}
      {...patchForLink}
      {...props}
    />
  );
}

export { Button, buttonVariants };